'use client'

import { KeyboardEvent, MutableRefObject, useId, useState } from 'react'
import { useLoanComparatorStore } from '@/app/stores/loan/loan-comparator.store'
import {
    applyPromoCode,
    removePromoCode,
    IPromoCodeResult,
    PromoCodeTooManyAttemptsError,
} from '@/app/endpoints/loanComparatorEndpoints'
import { useTracking } from '@/app/contexts/tracking-context'
import { LoanTrackingEvents } from '@/app/constants/loanTracking'
import { describePromoDiscount } from '@/app/utils/loan/promo-code'
import AppIcon from '@/app/components/atomic/AppIcon'

export const PROMO_CODE_INVALID_MESSAGE = "Ce code n'est pas valide ou a expiré. Vous pouvez continuer sans code."
const PROMO_CODE_TOO_MANY_ATTEMPTS_MESSAGE = 'Trop de tentatives, réessayez dans quelques minutes.'
const PROMO_CODE_UNAVAILABLE_MESSAGE = 'Le code promo est indisponible pour le moment. Vous pouvez continuer sans code.'

export type PromoCodeStep = 'coordonnees'

interface Props {
    token: string
    step: PromoCodeStep
    emailHint?: () => string | undefined
    pendingCodeRef?: MutableRefObject<string>
    onApplied?: (result: IPromoCodeResult) => void
    onRemoved?: () => void
    initiallyOpen?: boolean
    onCancel?: () => void
    appliedDescription?: string
    appliedClassName?: string
}

export default function LoanPromoCodeField({
    token,
    step,
    emailHint,
    pendingCodeRef,
    onApplied,
    onRemoved,
    initiallyOpen = false,
    onCancel,
    appliedDescription,
    appliedClassName = '',
}: Props) {
    const quote = useLoanComparatorStore((state) => state.quote)
    const updateQuote = useLoanComparatorStore((state) => state.quoteActions.update)
    const { track } = useTracking()
    const inputId = useId()
    const errorId = `${inputId}-error`

    const [editing, setEditing] = useState(initiallyOpen)
    const [code, setCode] = useState('')
    const [error, setError] = useState<string | null>(null)
    const [loading, setLoading] = useState(false)

    const appliedPromo = quote?.promo_code ?? null

    const setPendingCode = (value: string) => {
        setCode(value)
        if (pendingCodeRef) pendingCodeRef.current = value.trim()
    }

    const open = () => {
        track({ event: LoanTrackingEvents.promo.linkClick, promo_step: step })
        setEditing(true)
    }

    const cancel = () => {
        setPendingCode('')
        setError(null)
        setEditing(false)
        onCancel?.()
    }

    const apply = async () => {
        const normalized = code.trim().toUpperCase()
        if (!normalized || loading) return
        setLoading(true)
        setError(null)
        try {
            const result = await applyPromoCode(token, normalized, emailHint?.())
            track({
                event: LoanTrackingEvents.promo.apply,
                promo_step: step,
                promo_code: normalized,
                promo_status: result.valid ? 'valid' : 'invalid',
                ...(result.reason ? { promo_reason: result.reason } : {}),
            })
            if (!result.valid) {
                setError(PROMO_CODE_INVALID_MESSAGE)
                return
            }
            updateQuote({
                promo_code: {
                    code: result.code ?? normalized,
                    label: result.label,
                    discount_type: result.discountType ?? 'fixed_amount',
                    discount_value: result.discountValue ?? 0,
                },
                promo_discount_amount: result.discountAmount,
            })
            setPendingCode('')
            setEditing(false)
            onApplied?.(result)
        } catch (e) {
            setError(e instanceof PromoCodeTooManyAttemptsError ? PROMO_CODE_TOO_MANY_ATTEMPTS_MESSAGE : PROMO_CODE_UNAVAILABLE_MESSAGE)
        } finally {
            setLoading(false)
        }
    }

    const remove = async () => {
        if (loading) return
        setLoading(true)
        try {
            await removePromoCode(token)
            track({ event: LoanTrackingEvents.promo.remove, promo_step: step, promo_code: appliedPromo?.code })
            updateQuote({ promo_code: null, promo_discount_amount: null, promo_applied_at: null })
            onRemoved?.()
        } catch (e) {
            console.error('Error removing promo code:', e)
        } finally {
            setLoading(false)
        }
    }

    const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter') {
            event.preventDefault()
            void apply()
        }
    }

    if (appliedPromo) {
        return (
            <div
                className={`flex items-center justify-between gap-3 rounded-xl border border-[#CFE3D0] bg-[#EEF7EF] p-4 ${appliedClassName}`}
                role="status"
                aria-live="polite"
                data-testid="loan-promo-applied"
            >
                <div className="flex items-center gap-3">
                    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#2E7D32] text-white">
                        <AppIcon iconClassName="fa-check" size="sm" type="solid" />
                    </span>
                    <div>
                        <div className="text-[15px] font-bold text-pale">Code {appliedPromo.code} appliqué</div>
                        <div className="text-[14px] text-pale/80">{appliedDescription ?? describePromoDiscount(quote)}</div>
                    </div>
                </div>
                <button
                    type="button"
                    onClick={remove}
                    disabled={loading}
                    className="shrink-0 text-[13px] font-semibold text-pale/70 underline underline-offset-2 hover:text-pale disabled:opacity-50"
                >
                    Retirer
                </button>
            </div>
        )
    }

    if (!editing) {
        return (
            <button
                type="button"
                onClick={open}
                className="inline-flex items-center gap-2 text-[15px] font-bold text-[#425A9C] hover:text-rm-orange"
                data-testid="loan-promo-open"
            >
                <AppIcon iconClassName="fa-ticket" size="sm" type="regular" />
                J&apos;ai un code promo
            </button>
        )
    }

    return (
        <div className="flex flex-col gap-2" data-testid="loan-promo-editing">
            <div className="flex items-center justify-between">
                <label htmlFor={inputId} className="text-[15px] font-semibold text-pale">
                    Code promo
                </label>
                <button
                    type="button"
                    onClick={cancel}
                    className="text-[13px] text-pale/70 underline underline-offset-2 hover:text-pale"
                >
                    Annuler
                </button>
            </div>
            <div className="flex flex-col gap-2 sm:flex-row">
                <div className="relative flex-1">
                    <span className="pointer-events-none absolute top-1/2 left-4 -translate-y-1/2 text-pale/50">
                        <AppIcon iconClassName="fa-ticket" size="sm" type="regular" />
                    </span>
                    <input
                        id={inputId}
                        type="text"
                        value={code}
                        onChange={(event) => setPendingCode(event.target.value)}
                        onKeyDown={onKeyDown}
                        placeholder="Ex. BIENVENUE"
                        autoComplete="off"
                        autoCapitalize="characters"
                        spellCheck={false}
                        aria-invalid={error ? true : undefined}
                        aria-describedby={error ? errorId : undefined}
                        className={`min-h-[48px] w-full rounded-lg border bg-white py-2 pr-4 pl-11 text-[15px] uppercase text-pale outline-none placeholder:normal-case placeholder:font-normal placeholder:text-pale/40 focus:ring-2 focus:ring-[#7196FF]/40 ${
                            error ? 'border-[#E8531C]' : 'border-rm-gray-light'
                        }`}
                    />
                </div>
                <button
                    type="button"
                    onClick={apply}
                    disabled={loading || !code.trim()}
                    className="min-h-[48px] rounded-lg bg-[#7196FF] px-6 text-[15px] font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-50 sm:w-auto"
                >
                    {loading ? <AppIcon iconClassName="fa-spinner fa-spin" size="sm" type="regular" /> : 'Appliquer'}
                </button>
            </div>
            <div id={errorId} aria-live="polite" className="min-h-[1px] text-[13px] text-[#E8531C]">
                {error}
            </div>
        </div>
    )
}
